import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.addEventListener('pointerdown', this.#warmPreview, { capture: true, passive: true });
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    document.removeEventListener('pointerdown', this.#warmPreview, { capture: true });
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const isAdd = event.action === 'add';
    const wasOpen = Boolean(this.#themeDrawer?.isOpen);
    const shouldAutoOpen = this.hasAttribute('auto-open') && isAdd && !wasOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const closestModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );
    // GharGarden: adds from inside the side cart itself (the "under ₹69" row) aren't from another modal
    const sourceModal = closestModal === this.#dialog ? null : closestModal;

    // GharGarden instant side cart: open straight away and show the plant(s)
    // being added (from the add button's data-gg-preview), so the tap feels
    // instant. When Shopify answers, the real cart takes the preview's place.
    // GharGarden: a plant added from the add-on row leaves that row at once
    const tappedAddOn = isAdd && event.target instanceof Element ? event.target.closest('.cart-upsell__item') : null;
    if (tappedAddOn instanceof HTMLElement) tappedAddOn.hidden = true;

    const showPending = isAdd && !sourceModal && (shouldAutoOpen || wasOpen);
    const openedWhileEmpty = showPending && this.#isCartEmpty();
    const pending = showPending ? this.#showPending(event, openedWhileEmpty) : null;
    if (shouldAutoOpen && !sourceModal) this.#themeDrawer?.open();

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let failSafe;
    const finish = () => {
      clearTimeout(failSafe);
      // Only ever remove our own preview (never an element the re-draw has reused)
      if (pending?.isConnected && pending.matches('aside.gg-pending')) pending.remove();
      if (!this.querySelector('aside.gg-pending')) this.classList.remove('gg-adding');
    };
    // If Shopify never answers, don't leave the preview up
    if (pending) {
      failSafe = setTimeout(() => {
        finish();
        if (this.#isCartEmpty()) this.#themeDrawer?.close();
      }, 10000);
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (detail?.didError) {
          if (tappedAddOn instanceof HTMLElement) tappedAddOn.hidden = false;
          this.#failPending(pending, finish, openedWhileEmpty);
          settle();
          return;
        }

        this.#finishAfterRender(pending, finish);
        if (!shouldAutoOpen) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (tappedAddOn instanceof HTMLElement) tappedAddOn.hidden = false;
        this.#failPending(pending, finish, openedWhileEmpty);
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
    if (!event.promise) finish();
  };

  /**
   * GharGarden: start loading the photos for the instant side cart as soon as
   * a finger touches an Add button (~0.1s before the tap lands). Nothing is
   * loaded unless someone is actually about to add.
   * @param {PointerEvent} event
   */
  #warmPreview = (event) => {
    const holder = event.target instanceof Element ? event.target.closest('[data-gg-preview]') : null;
    if (!(holder instanceof HTMLElement) || holder.dataset.ggWarm) return;
    holder.dataset.ggWarm = '1';
    try {
      for (const x of JSON.parse(holder.getAttribute('data-gg-preview') || '[]').slice(0, 4)) {
        if (x.i) new Image().src = x.i;
      }
    } catch {
      // nothing to warm
    }
  };

  /**
   * GharGarden: shows the plant(s) being added at once, before Shopify confirms.
   * On an empty cart it looks like the finished cart (title, delivery bar,
   * plants, total), so when the real cart arrives only small things change.
   * It is an <aside>, not a <div>: the theme's re-draw matches elements by tag
   * and position, and would otherwise turn this preview into the delivery bar.
   * @param {any} event - the cart lines update event (its lines hold the variant ids)
   * @param {boolean} hideCart - the cart is empty, so hide the empty-cart message meanwhile
   * @returns {HTMLElement | null}
   */
  #showPending(event, hideCart) {
    const inner = this.querySelector('.cart-drawer__inner');
    if (!inner) return null;

    const source = event.target instanceof Element ? event.target : null;
    /** @type {Array<{id: number, t?: string, i?: string, p?: number, c?: number}>} */
    let known = [];
    try {
      known = JSON.parse(source?.closest('[data-gg-preview]')?.getAttribute('data-gg-preview') || '[]');
    } catch {
      known = [];
    }
    const byId = new Map(known.map((x) => [String(x.id), x]));
    const lines = (event.lines || []).map((/** @type {any} */ l) => {
      const id = String(l.merchandiseId || '').split('/').pop() || '';
      return { ...(byId.get(id) || {}), id, q: Number(l.quantity) || 1 };
    });
    if (!lines.length) lines.push({ id: '', q: 1 });

    const money = (/** @type {number} */ cents) =>
      '₹' +
      (cents % 100
        ? (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : (cents / 100).toLocaleString('en-IN'));
    const el = (/** @type {string} */ tag, /** @type {string} */ cls, /** @type {string | undefined} */ text) => {
      const node = document.createElement(tag);
      if (cls) node.className = cls;
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const total = lines.reduce((sum, x) => sum + (x.p || 0) * x.q, 0);
    const mrp = lines.reduce((sum, x) => sum + Math.max(x.c || 0, x.p || 0) * x.q, 0);
    const count = lines.reduce((sum, x) => sum + x.q, 0);

    const panel = el('aside', hideCart ? 'gg-pending gg-pending--full' : 'gg-pending');
    panel.setAttribute('aria-label', 'Adding to your cart');

    if (hideCart) {
      const head = el('p', 'gg-pending__head', 'Cart ');
      head.append(el('span', 'gg-pending__count', String(count)));
      panel.append(head);

      if (total > 0) {
        const bar = el('div', 'cart-free-shipping cart-rewards');
        if (this.#drawBar(bar, total, money)) panel.append(bar);
      }
    }

    // A plant that's already in the cart: bump its quantity in place rather than adding a second row,
    // and move the delivery bar on straight away. Shopify's real cart replaces both a moment later.
    /** @type {Set<object>} */
    const bumped = new Set();
    if (!hideCart) {
      for (const x of lines) {
        if (!x.id) continue;
        const row = this.querySelector(`[data-key^="${CSS.escape(x.id)}:"]`);
        const input = row?.querySelector('input[name="updates[]"]');
        if (row && input instanceof HTMLInputElement) {
          input.value = String((Number(input.value) || 0) + x.q);
          row.classList.add('gg-bump');
          bumped.add(x);
        }
      }
      const totalEl = this.querySelector('.cart-totals__total-value');
      const current = Number(String(totalEl?.getAttribute('value') || totalEl?.textContent || '').replace(/[^0-9.]/g, ''));
      const bar = [...this.querySelectorAll('.cart-free-shipping')].find((b) => !b.closest('aside'));
      if (bar && current > 0 && total > 0) this.#drawBar(bar, Math.round(current * 100) + total, money);
    }

    const shown = lines.filter((x) => !bumped.has(x));
    const list = el('ul', 'gg-pending__list');
    for (const x of shown.slice(0, 4)) {
      const row = el('li', 'gg-pending__row');
      const pic = document.createElement(x.i ? 'img' : 'span');
      pic.className = 'gg-pending__img';
      if (x.i && pic instanceof HTMLImageElement) {
        pic.src = x.i;
        pic.alt = '';
        pic.width = 56;
        pic.height = 56;
      }
      const text = el('div', 'gg-pending__text');
      text.append(el('b', 'gg-pending__name', x.t || 'Your plant'));
      if (x.p) {
        const price = el('span', 'gg-pending__price', money(x.p));
        if (x.c && x.c > x.p) {
          price.append(' ', el('s', '', money(x.c)));
          const off = Math.floor(((x.c - x.p) * 100) / x.c);
          if (off >= 5) price.append(' ', el('span', 'cart-items__off', `${off}% off`));
        }
        text.append(price);
        if (x.q > 1) text.append(el('span', 'gg-pending__qty', `Qty ${x.q}`));
      }
      row.append(pic, text);
      if (x.p) row.append(el('span', 'gg-pending__line-total', money(x.p * x.q)));
      list.append(row);
    }
    if (shown.length > 4) list.append(el('li', 'gg-pending__more', `+ ${shown.length - 4} more`));
    if (shown.length) panel.append(list);

    if (hideCart && total > 0) {
      const foot = el('div', 'gg-pending__foot');
      const row = el('p', 'gg-pending__total', 'Estimated total');
      row.append(el('b', '', money(total)));
      foot.append(row);
      if (mrp > total) foot.append(el('p', 'gg-pending__save', `You save ${money(mrp - total)}`));
      foot.append(el('p', 'gg-pending__cta', 'Getting checkout ready…'));
      panel.append(foot);
    } else {
      panel.append(el('span', 'gg-pending__line'));
    }

    if (hideCart) {
      const header = inner.querySelector(':scope > .theme-drawer__header');
      if (header) header.after(panel);
      else inner.prepend(panel);
      this.classList.add('gg-adding');
    } else {
      // On a cart that already has plants, show the new one at the top of the list
      (this.querySelector('.cart-drawer__items') || inner).prepend(panel);
    }
    return panel;
  }

  /**
   * GharGarden: draws the rewards bar (free delivery, then the rewards from the
   * theme settings) for a cart total in paise — the same markup as
   * snippets/cart-free-shipping-bar.liquid. Returns false if the bar is off.
   * @param {Element} bar - a .cart-free-shipping element
   * @param {number} total
   * @param {(cents: number) => string} money
   */
  #drawBar(bar, total, money) {
    if (!(Number(this.dataset.ggFree) > 0)) return false;
    /** @type {Array<{at: number, label: string, icon: string}>} */
    let ms = [];
    try {
      ms = JSON.parse(this.dataset.ggMilestones || '[]');
    } catch {
      ms = [];
    }
    if (!ms.length) return false;

    const n = ms.length;
    let fill = 100;
    let next = -1;
    let prevAt = 0;
    ms.forEach((m, i) => {
      const p = ((i + 1) * 100) / n;
      const prevP = (i * 100) / n;
      if (next === -1 && total < m.at) {
        next = i;
        fill = prevP + ((total - prevAt) * (p - prevP)) / (m.at - prevAt);
      }
      prevAt = m.at;
    });

    const esc = (/** @type {string} */ s) =>
      String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);
    // The templates are the theme's own translations (escaped server-side); the values are ours
    const msg =
      next === -1
        ? '<span class="cart-free-shipping__emoji" aria-hidden="true">🎉</span>' +
          (this.dataset.ggBarDone || '').replace('__REWARDS__', esc(ms.map((m) => m.label.toLowerCase()).join(' + ')))
        : (this.dataset.ggBarTodo || '')
            .replace('__AMT__', money(ms[next].at - total))
            .replace('__REWARD__', esc(`${ms[next].icon} ${ms[next].label}`));
    const dots = ms
      .map((m, i) => {
        const done = total >= m.at;
        const cls = `cart-rewards__dot${done ? ' is-done' : ''}${i === next ? ' is-next' : ''}`;
        return `<span class="${cls}" style="left: ${(((i + 1) * 100) / n).toFixed(1)}%;" aria-hidden="true">${done ? '✓' : esc(m.icon)}</span>`;
      })
      .join('');
    const labels = ms
      .map((m, i) => {
        const cls = `cart-rewards__label${total >= m.at ? ' is-done' : ''}${i === n - 1 ? ' is-last' : ''}`;
        return `<li class="${cls}" style="left: ${(((i + 1) * 100) / n).toFixed(1)}%;"><b>${money(m.at)}</b><span>${esc(m.label)}</span></li>`;
      })
      .join('');

    bar.className = `cart-free-shipping cart-rewards${next === -1 ? ' cart-free-shipping--unlocked' : ''}`;
    bar.innerHTML =
      `<p class="cart-free-shipping__message">${msg}</p>` +
      `<div class="cart-rewards__track" role="progressbar" aria-valuenow="${Math.round(fill)}" aria-valuemin="0" aria-valuemax="100">` +
      `<div class="cart-rewards__fill" style="width: ${fill.toFixed(1)}%;"></div>${dots}</div>` +
      `<ol class="cart-rewards__labels" role="list">${labels}</ol>`;
    return true;
  }

  /**
   * GharGarden: removes the preview once the real cart has rendered (or after
   * 1.5s at most), so the side cart never flashes empty in between.
   * @param {HTMLElement | null} pending
   * @param {() => void} finish
   */
  #finishAfterRender(pending, finish) {
    if (!pending) return finish();
    const inner = this.querySelector('.cart-drawer__inner');
    let done = false;
    /** @type {MutationObserver | undefined} */
    let observer;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let cap;
    const end = () => {
      if (done) return;
      done = true;
      observer?.disconnect();
      clearTimeout(cap);
      requestAnimationFrame(finish);
    };
    if (inner) {
      observer = new MutationObserver((records) => {
        // Our own preview going away doesn't count as the cart rendering
        const onlyPreview = records.every(
          (r) => r.type === 'childList' && [...r.addedNodes, ...r.removedNodes].every((n) => n === pending)
        );
        if (!onlyPreview) end();
      });
      observer.observe(inner, { childList: true, subtree: true, characterData: true });
    }
    cap = setTimeout(end, 1500);
  }

  /**
   * GharGarden: the add failed. Drop the preview, close the side cart if it
   * has nothing else in it, and say so in a short message. The message lives
   * outside the cart's markup because the theme re-draws the cart after a
   * failed add and would wipe anything placed inside it.
   * @param {HTMLElement | null} pending
   * @param {() => void} finish
   * @param {boolean} closeIfEmpty
   */
  #failPending(pending, finish, closeIfEmpty) {
    if (closeIfEmpty && this.#isCartEmpty()) {
      // Close with the preview still showing, so the empty-cart text never flashes
      const status = pending?.querySelector('.gg-pending__status');
      if (status) status.textContent = "Couldn't add this";
      this.#themeDrawer?.close();
      setTimeout(finish, 400);
    } else {
      finish();
    }
    if (!pending) return;
    const host = this.#themeDrawer?.isOpen && this.#dialog ? this.#dialog : document.body;
    const toast = document.createElement('p');
    toast.className = 'gg-toast';
    toast.setAttribute('role', 'alert');
    toast.textContent = "Sorry, this couldn't be added. It may have just sold out.";
    host.append(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * Empties the cart via the Cart AJAX API. Bound from the drawer's "Clear"
   * button (`on:click="/clearCart"`) - lives here, on the already-loaded
   * drawer component, rather than a separate custom element, since that
   * button is rendered inside content that gets replaced whenever the cart
   * updates and a freshly-injected `<script>` tag for a standalone
   * component isn't guaranteed to have executed yet by the time it's clicked.
   * @param {Event} event
   */
  async clearCart(event) {
    event.preventDefault();

    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (button) button.disabled = true;

    const deferredPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [],
        promise: deferredPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_clear_url, fetchConfig('json', { body: '{}' }));
      const data = await response.json();

      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          source: 'cart-drawer-component',
          didError: false,
        },
      });
    } catch (error) {
      deferredPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Failed to clear cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      if (button) button.disabled = false;
    }
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
